// Helpers for the To / Cc / Bcc / Reply-To fields on the email composers.
// Addresses travel as raw comma-separated strings (matching how Firestore stores them on a
// message and how the thread renders them) and are only split into lists at the wire boundary.

const SEPARATORS = /[,;]/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Pull the bare address out of `Jane Doe <jane@example.com>`. */
export const extractAddrSpec = (value) => {
    const trimmed = (value || '').trim();
    if (trimmed.includes('<') && trimmed.includes('>')) {
        return trimmed.slice(trimmed.indexOf('<') + 1, trimmed.lastIndexOf('>')).trim();
    }
    return trimmed;
};

/**
 * Accepts a comma/semicolon separated string or an array, and returns a trimmed list with blanks
 * dropped and duplicates removed case-insensitively, preserving the order the user typed.
 */
export const parseAddressList = (value) => {
    if (!value) return [];
    const raw = Array.isArray(value) ? value : [value];

    const seen = new Set();
    const result = [];
    raw.forEach((entry) => {
        if (!entry) return;
        String(entry).split(SEPARATORS).forEach((token) => {
            const address = token.trim();
            if (!address) return;
            const key = extractAddrSpec(address).toLowerCase();
            if (seen.has(key)) return;
            seen.add(key);
            result.push(address);
        });
    });
    return result;
};

export const formatAddressList = (addresses) => (addresses || []).join(', ');

export const isValidEmail = (value) => EMAIL_PATTERN.test(extractAddrSpec(value));

export const validateAddressList = (value) => {
    const addresses = parseAddressList(value);
    return {
        valid: addresses.filter(isValidEmail),
        invalid: addresses.filter((address) => !isValidEmail(address)),
    };
};

/**
 * Seed values for the reply composer.
 *
 * To  = the last inbound message's Reply-To if the sender gave us one, otherwise the conversation
 *       contact. Bcc and Reply-To always start blank.
 * Cc  = the last inbound message's Cc, minus our own channel address and minus anyone already in To.
 *
 * `lastInboundMessage` is a Firestore doc, so its keys are snake_case; `conversation` comes from
 * GraphQL, so its keys are camelCase.
 */
export const buildReplyRecipients = ({ lastInboundMessage, conversation } = {}) => {
    const replyToAddresses = parseAddressList(lastInboundMessage?.reply_to);
    const toList = replyToAddresses.length
        ? replyToAddresses
        : parseAddressList(conversation?.contactEmail);

    const excluded = new Set(toList.map((address) => extractAddrSpec(address).toLowerCase()));
    const ownAddress = extractAddrSpec(conversation?.channel?.sendAs || '').toLowerCase();
    if (ownAddress) excluded.add(ownAddress);

    const ccList = parseAddressList(lastInboundMessage?.cc)
        .filter((address) => !excluded.has(extractAddrSpec(address).toLowerCase()));

    return {
        to: formatAddressList(toList),
        cc: formatAddressList(ccList),
        bcc: '',
        replyTo: '',
    };
};

export const EMPTY_RECIPIENTS = { to: '', cc: '', bcc: '', replyTo: '' };
